import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, FileSpreadsheet, Search } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  accountingApi,
  type AccountingAccountReport,
} from '@/lib/api/accounting';
import { formatRial } from '@/lib/formatMoney';
import { exportAccountTurnoverExcel } from '@/lib/exportAccountTurnoverExcel';

const AccountingAccountReportPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [accountCode, setAccountCode] = React.useState(() => searchParams.get('code')?.trim() ?? '');
  const [fromDate, setFromDate] = React.useState('');
  const [toDate, setToDate] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [report, setReport] = React.useState<AccountingAccountReport | null>(null);
  const [projectNameMap, setProjectNameMap] = React.useState<Record<number, string>>({});

  React.useEffect(() => {
    accountingApi
      .lookups()
      .then((lookups) => {
        const projectMap: Record<number, string> = {};
        (lookups.projects || []).forEach((p) => {
          projectMap[p.id] = p.label;
        });
        setProjectNameMap(projectMap);
      })
      .catch(() => {
        setProjectNameMap({});
      });
  }, []);

  const fetchReport = React.useCallback(() => {
    if (!accountCode) return;
    setLoading(true);
    accountingApi
      .accountingAccountReport({
        account_code: accountCode,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      })
      .then(setReport)
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [accountCode, fromDate, toDate]);

  React.useEffect(() => {
    const initialCode = searchParams.get('code')?.trim();
    if (!initialCode) return;
    setLoading(true);
    accountingApi
      .accountingAccountReport({ account_code: initialCode })
      .then(setReport)
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [searchParams]);

  const handleExportExcel = async () => {
    if (!report) return;
    setExporting(true);
    try {
      await exportAccountTurnoverExcel({
        report,
        accountTitle: report.accountTitle || '',
        projectNameMap,
        fromDate: fromDate || report.fromDate || undefined,
        toDate: toDate || report.toDate || undefined,
      });
    } catch {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="گزارش گردش حساب"
        description="گزارش بدهکار/بستانکار؛ برای حساب کل و معین، گردش زیرمجموعه‌ها هم در جمع لحاظ می‌شود."
        actions={
          <Button asChild variant="outline" className="gap-2">
            <Link to="/">
              <ArrowRight className="h-4 w-4" />
              بازگشت
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">فیلتر گزارش</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs text-slate-600">کد حساب</label>
            <Input
              dir="ltr"
              className="font-mono"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              placeholder="مثلاً 110101 یا 11"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">از تاریخ</label>
            <Input value={fromDate} onChange={(e) => setFromDate(e.target.value)} placeholder="1405/01/01" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">تا تاریخ</label>
            <Input value={toDate} onChange={(e) => setToDate(e.target.value)} placeholder="1405/12/29" />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button type="button" className="gap-2" onClick={fetchReport} disabled={!accountCode || loading}>
              <Search className="h-4 w-4" />
              {loading ? 'در حال دریافت...' : 'دریافت گزارش'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <LoadingLock loading={loading}>
      {report ? (
        <>
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">
                  خلاصه حساب {report.accountCode} — {report.accountTitle || '—'}
                </CardTitle>
                {report.includeSubAccounts ? (
                  <p className="mt-1 text-sm text-slate-600">
                    شامل گردش حساب‌های معین و تفصیلی زیرمجموعه ({report.rows.length} ردیف)
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                className="gap-2 shrink-0 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                onClick={handleExportExcel}
                disabled={exporting}
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exporting ? 'در حال ساخت فایل...' : 'خروجی اکسل'}
              </Button>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded border p-3 text-sm">جمع بدهکار: {formatRial(report.totalDebit || 0)}</div>
              <div className="rounded border p-3 text-sm">جمع بستانکار: {formatRial(report.totalCredit || 0)}</div>
              <div className="rounded border p-3 text-sm font-semibold">مانده: {formatRial(report.balance || 0)}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">ریز گردش‌ها</CardTitle>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="gap-2 text-emerald-700"
                onClick={handleExportExcel}
                disabled={exporting}
              >
                <FileSpreadsheet className="h-4 w-4" />
                اکسل
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full min-w-[1040px] text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-slate-700">
                      <th className="border px-2 py-2 text-right">شماره سند</th>
                      <th className="border px-2 py-2 text-right">تاریخ</th>
                      <th className="border px-2 py-2 text-right">شرح عملیات</th>
                      <th className="border px-2 py-2 text-right">پروژه</th>
                      {report.includeSubAccounts ? (
                        <th className="border px-2 py-2 text-right">کد حساب</th>
                      ) : null}
                      <th className="border px-2 py-2 text-right">عنوان حساب</th>
                      <th className="border px-2 py-2 text-right">بدهکار</th>
                      <th className="border px-2 py-2 text-right">بستانکار</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((r) => (
                      <tr key={r.lineId}>
                        <td className="border px-2 py-2">{r.docNo}</td>
                        <td className="border px-2 py-2">{r.docDate}</td>
                        <td className="border px-2 py-2">{r.operationDescription || '—'}</td>
                        <td className="border px-2 py-2">{(r.projectId && projectNameMap[r.projectId]) || '—'}</td>
                        {report.includeSubAccounts ? (
                          <td className="border px-2 py-2 font-mono">{r.accountCode}</td>
                        ) : null}
                        <td className="border px-2 py-2">{r.accountTitle || '—'}</td>
                        <td className="border px-2 py-2 font-mono">{formatRial(r.debit || 0)}</td>
                        <td className="border px-2 py-2 font-mono">{formatRial(r.credit || 0)}</td>
                      </tr>
                    ))}
                    {report.rows.length === 0 ? (
                      <tr>
                        <td
                          className="border px-2 py-3 text-center text-slate-500"
                          colSpan={report.includeSubAccounts ? 8 : 7}
                        >
                          گردش برای این حساب پیدا نشد.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
      </LoadingLock>
    </div>
  );
};

export default AccountingAccountReportPage;
